import clsx from "clsx";
import {
  ComponentPropsWithoutRef,
  ElementType,
  PropsWithChildren,
} from "react";
import { WrapperDrawnBorder } from "../wrappers/WrapperDrawnBorder";
import Icon from "./Icon";
import * as styles from "./Button.css";
import LoadingDots from "./LoadingDots";
import { soundButtonActive } from "~/lib/hooks/sounds/soundButtonActive";
import { soundButtonDisabled } from "~/lib/hooks/sounds/soundButtonDisabled";

export type DefaultButtonProps = {
  icon?: ElementType;
  isLoading?: boolean;
} & styles.ThemeVariants &
  styles.LayoutVariants &
  styles.WrapperVariants;

export type ButtonProps = ComponentPropsWithoutRef<"button"> &
  DefaultButtonProps;

export type ButtonChildProps = PropsWithChildren & DefaultButtonProps;

export const Button = ({
  className,
  children,
  theme = "light",
  size = "lg",
  layout = "right",
  width = "fit",
  isDisabled = false,
  isLoading = false,
  icon,
  onClick,
  ...props
}: ButtonProps) => {
  const button = (
    <button
      style={{ border: "0px" }}
      disabled={isDisabled}
      type="button"
      className={clsx(
        className,
        styles.button({
          theme: theme,
          size: size,
          hasIcon: icon && !isLoading ? true : false,
        })
      )}
      onClick={(e) => (
        !isDisabled && soundButtonActive(), onClick && onClick(e)
      )}
      {...props}
    >
      <ButtonChild
        icon={icon}
        layout={layout}
        isLoading={isLoading}
        theme={theme}
      >
        {children}
      </ButtonChild>
    </button>
  );
  return (
    <div
      className={styles.wrapper({ width: width })}
      onClick={() => isDisabled && soundButtonDisabled()}
    >
      {theme === "text" ? (
        button
      ) : (
        <WrapperDrawnBorder
          className={styles.wrapper({ isDisabled: isDisabled, width: width })}
        >
          {button}
        </WrapperDrawnBorder>
      )}
    </div>
  );
};

export const ButtonChild = ({ children, ...props }: ButtonChildProps) => {
  const { layout, icon, isLoading, size, theme } = props;

  return (
    <div className={styles.children({ layout: layout, size: "sm" })}>
      {isLoading ? (
        <LoadingDots theme={theme == "dark" ? "light" : "dark"} />
      ) : (
        <>
          {children}
          {icon && (
            <div className={styles.iconContainer}>
              <div className={styles.icons}>
                <Icon svg={icon} size="15px" />
                <Icon svg={icon} size="15px" />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};
